import { useEffect, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckEnvironment, SupportedEnvironments } from '../../config-global';
import { hasBrandCookie } from '../../utils/brandCookieUtils';
import { PATH_DEV } from '../../routes/paths';
import { useAuthContext } from '../../auth/useAuthContext';

interface BrandGuardProps {
  children: ReactNode;
}

/**
 * BrandGuard component that redirects to brand selector if no brand is set in development
 * Only active in development builds
 */
export default function BrandGuard({ children }: BrandGuardProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isInitialized } = useAuthContext();

  useEffect(() => {
    // Only check in development environment
    if (!CheckEnvironment(SupportedEnvironments.Dev)) {
      return;
    }

    // Wait for auth to initialize
    if (!isInitialized) {
      return;
    }

    // Don't redirect if we're already on the brand selector page
    if (location.pathname === PATH_DEV.brand) {
      return;
    }

    // Don't redirect if we're on auth-related pages
    const authPaths = ['/user/login', '/user/action', '/user/email-verified'];
    if (authPaths.some((path) => location.pathname.startsWith(path))) {
      return;
    }

    // Check if brand is set in cookie
    // For unauthenticated users, always require cookie
    // For authenticated users, they might have JWT claims but we still prefer cookie for dev
    if (!hasBrandCookie()) {
      navigate(PATH_DEV.brand, { replace: true });
    }
  }, [navigate, location.pathname, isAuthenticated, isInitialized]);

  return <>{children}</>;
}
